import React, { useCallback, useEffect, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import CopyButton from './CopyButton';
import AdminBackupCloud from './AdminBackupCloud';
import { formatBytes } from './AdminServer';
import './AdminNewsletter.css';
import './AdminServer.css';

const STATUS_LABELS = { ok: 'Готова', failed: 'Ошибка', running: 'Делается…' };
const KIND_LABELS = { auto: 'ежедневная', manual: 'вручную', hourly: 'почасовая', logs: 'логи' };
const REMOTE_LABELS = { uploaded: 'в облаке', pending: 'ждёт выгрузки', failed: 'ошибка выгрузки', deleted: 'удалена из облака' };
const FILTERS = [['', 'Все'], ['daily', 'Ежедневные'], ['hourly', 'Почасовые'], ['logs', 'Логи']];

function formFromSettings(st) {
    return {
        enabled: st.enabled, hour: st.hour, keep_days: st.keep_days, keep_months: st.keep_months,
        hourly: st.hourly, keep_hours: st.keep_hours, logs: st.logs,
        remote_keep_days: st.remote_keep_days, remote_keep_months: st.remote_keep_months,
        remote_keep_hours: st.remote_keep_hours, dir: st.dir_custom ? st.dir : '',
    };
}

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function ago(value) {
    if (!value) return null;
    const hours = (Date.now() - new Date(value).getTime()) / 3600000;
    if (hours < 1) return 'меньше часа назад';
    if (hours < 48) return `${Math.round(hours)} ч. назад`;
    return `${Math.round(hours / 24)} дн. назад`;
}

function Commands({ title, text }) {
    return (
        <div className="bkp-cmd">
            <div className="bkp-cmd__title">{title}</div>
            <pre>{text}</pre>
            <CopyButton text={text} className="btn btn--secondary btn--sm" />
        </div>
    );
}

// Резервные копии базы (пользователи, подписки, платежи, опросы…) и картинок.
function AdminBackups() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [showRestore, setShowRestore] = useState(false);
    const [filter, setFilter] = useState('');

    const apply = (d) => {
        setData(d);
        setForm(formFromSettings(d.settings));
    };

    const load = useCallback(async () => {
        try {
            const res = await api.get('/admin/backups', { params: filter ? { kind: filter } : {} });
            setData(res.data);
            setForm((prev) => prev || formFromSettings(res.data.settings));
            setError(null);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось загрузить резервные копии'));
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);
    const running = data?.running || data?.items?.some((i) => i.status === 'running');
    useEffect(() => {
        if (!running) return undefined;
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, [running, load]);

    const createNow = async () => {
        setBusy(true);
        setNotice(null);
        try {
            const res = await api.post('/admin/backups');
            setData(res.data);
            setNotice({ type: 'success', text: 'Копия делается. Обычно это занимает до минуты.' });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось запустить копирование') });
        } finally {
            setBusy(false);
        }
    };

    const saveSettings = async () => {
        setBusy(true);
        setNotice(null);
        try {
            const res = await api.put('/admin/backups/settings', {
                ...form, hour: Number(form.hour), keep_days: Number(form.keep_days), keep_months: Number(form.keep_months),
                keep_hours: Number(form.keep_hours), remote_keep_days: Number(form.remote_keep_days),
                remote_keep_months: Number(form.remote_keep_months), remote_keep_hours: Number(form.remote_keep_hours),
                dir: form.dir.trim(),
            });
            apply(res.data);
            setNotice({ type: 'success', text: 'Настройки сохранены.' });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось сохранить настройки') });
        } finally {
            setBusy(false);
        }
    };

    const download = async (item, part) => {
        setDownloading(`${item.id}-${part}`);
        try {
            const res = await api.get(`/admin/backups/${item.id}/download`, { params: { part }, responseType: 'blob', timeout: 30 * 60 * 1000 });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = part === 'static' ? item.static_filename : item.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось скачать копию') });
        } finally {
            setDownloading(null);
        }
    };

    const remove = async (item) => {
        const where = item.remote_status === 'uploaded' ? ' с сервера и из облака' : ' с сервера';
        if (!window.confirm(`Удалить копию от ${formatDate(item.created_at)}${where}?`)) return;
        try {
            const res = await api.delete(`/admin/backups/${item.id}`);
            setData(res.data);
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось удалить копию') });
        }
    };

    if (error) return <section className="admin-main-card"><h2 className="admin-main-card__title">Резервные копии</h2><div className="nl-body"><div className="admin-notice admin-notice--error">{error}</div></div></section>;
    if (!data || !form) return <section className="admin-main-card"><h2 className="admin-main-card__title">Резервные копии</h2><div className="nl-body nl-muted">Загрузка…</div></section>;

    const hoursSince = data.last_ok_at ? (Date.now() - new Date(data.last_ok_at).getTime()) / 3600000 : null;
    const fresh = hoursSince != null && hoursSince < 36;
    const labels = data.labels || {};

    return (
        <section className="admin-main-card">
            <div className="nl-head">
                <h2 className="admin-main-card__title">Резервные копии</h2>
                <button type="button" className="btn btn--primary btn--sm" onClick={createNow} disabled={busy || running}>
                    {running ? 'Копия делается…' : 'Сделать копию сейчас'}
                </button>
            </div>
            <div className="nl-body">
                <p className="nl-hint">
                    Копия — это вся база СВОД: пользователи, подписки и платежи, промокоды, компании, приглашения,
                    опросы и ответы, рассылки и настройки. Отдельным архивом сохраняются загруженные картинки и логотипы.
                    Копии делаются автоматически каждый день и хранятся на сервере в папке <code>{data.settings.dir}</code>.
                </p>

                <div className={`srv-alert ${fresh ? 'bkp-ok' : 'srv-alert--danger'}`}>
                    {data.last_ok_at
                        ? <>Последняя удачная копия: <strong>{formatDate(data.last_ok_at)}</strong> ({ago(data.last_ok_at)}).</>
                        : <>Удачных копий ещё нет — нажмите «Сделать копию сейчас».</>}
                    {' '}
                    {data.settings.enabled
                        ? <>Автоматически — каждый день после {String(data.settings.hour).padStart(2, '0')}:00 (время сервера).</>
                        : <b>Автоматические копии выключены.</b>}
                    {data.settings.hourly && <> Почасовые копии — {data.last_hourly_at ? `последняя ${formatDate(data.last_hourly_at)}` : 'ещё не было'} (делаются, только если в базе что-то менялось).</>}
                    {data.total_size > 0 && <> На сервере копии занимают {formatBytes(data.total_size)}{data.disk_free != null && <>, свободно на диске {formatBytes(data.disk_free)}</>}.</>}
                    {data.remote_total > 0 && <> В облаке — {formatBytes(data.remote_total)}.</>}
                </div>
                {data.database === 'postgresql' && data.pg_dump === false && (
                    <div className="srv-alert srv-alert--danger">
                        На сервере нет программы pg_dump — копии базы не получатся.
                        <Commands title="Установите её:" text="sudo apt install -y postgresql-client" />
                    </div>
                )}
                {!data.offsite.configured && (
                    <div className="srv-alert srv-alert--warning">
                        Копии лежат только на этом сервере. Они спасут от ошибки или случайного удаления, но не от поломки
                        самого сервера. Настройте выгрузку в облако ниже (Яндекс Диск или хранилище S3).
                    </div>
                )}

                {notice && <div className={`admin-notice admin-notice--${notice.type}`} role="status">{notice.text}</div>}

                <div className="nl-recipients-head">
                    <h3 className="nl-block__title">Копии</h3>
                    <div className="nl-seg" role="radiogroup" aria-label="Какие копии показать">
                        {FILTERS.map(([v, label]) => (
                            <button key={v || 'all'} type="button" role="radio" aria-checked={filter === v}
                                    className={filter === v ? 'is-active' : ''} onClick={() => setFilter(v)}>{label}</button>
                        ))}
                    </div>
                </div>
                <div className="admin-table-scroll">
                    <table className="nl-table bkp-table">
                        <thead><tr><th>Дата</th><th>Состояние</th><th>Размер</th><th>Что внутри</th><th>В облаке</th><th aria-label="Действия" /></tr></thead>
                        <tbody>
                            {data.items.length === 0 && <tr><td colSpan={6} className="nl-muted">Копий пока нет</td></tr>}
                            {data.items.map((item) => (
                                <tr key={item.id}>
                                    <td className="mlog-time">
                                        {formatDate(item.created_at)}
                                        <div className="nl-muted srv-small">{KIND_LABELS[item.kind] || item.kind}</div>
                                        <div className="nl-muted srv-small bkp-file">{item.filename}</div>
                                    </td>
                                    <td>
                                        <span className={`nl-rstatus bkp-status--${item.status}`}>{STATUS_LABELS[item.status] || item.status}</span>
                                        {item.status === 'ok' && !item.present && <div className="nl-muted srv-small">на сервере удалена по сроку</div>}
                                        {item.error && <div className="nl-error-text">{item.error}</div>}
                                        {item.duration_sec != null && item.status === 'ok' && <div className="nl-muted srv-small">за {item.duration_sec} с</div>}
                                    </td>
                                    <td>
                                        {item.size != null ? formatBytes(item.size) : '—'}
                                        {item.static_size != null && <div className="nl-muted srv-small">+ картинки {formatBytes(item.static_size)}</div>}
                                    </td>
                                    <td className="srv-small">
                                        {item.kind === 'logs'
                                            ? <>строк: {item.details?.lines ?? '—'}{item.details?.nginx ? ' · с логами nginx' : ''}</>
                                            : Object.entries(item.counts || {}).map(([k, v]) => `${v} ${labels[k] || k}`).join(' · ') || '—'}
                                        {item.details?.tables != null && <div className="nl-muted">таблиц: {item.details.tables}</div>}
                                    </td>
                                    <td className="srv-small">
                                        {item.remote_status
                                            ? <span className={`nl-rstatus bkp-remote--${item.remote_status}`}>{REMOTE_LABELS[item.remote_status] || item.remote_status}</span>
                                            : '—'}
                                        {item.uploaded_at && item.remote_status === 'uploaded' && <div className="nl-muted">{formatDate(item.uploaded_at)}</div>}
                                        {item.remote_error && <div className="nl-error-text">{item.remote_error}</div>}
                                    </td>
                                    <td className="nl-row-actions">
                                        {item.present && (
                                            <>
                                                <button type="button" className="btn btn--secondary btn--sm" onClick={() => download(item, 'db')} disabled={downloading != null}>
                                                    {downloading === `${item.id}-db` ? 'Скачиваем…' : 'Скачать базу'}
                                                </button>
                                                {item.static_filename && (
                                                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => download(item, 'static')} disabled={downloading != null}>
                                                        {downloading === `${item.id}-static` ? 'Скачиваем…' : 'Картинки'}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                        {item.status !== 'running' && (
                                            <button type="button" className="btn btn--danger-outline btn--sm" onClick={() => remove(item)}>Удалить</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="nl-block bkp-settings">
                    <h3 className="nl-block__title">Расписание и хранение</h3>
                    <label className="nl-check">
                        <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                        Делать копию автоматически каждый день
                    </label>
                    <div className="nl-inline bkp-row">
                        <span>после</span>
                        <select value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} aria-label="Час">
                            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                        </select>
                        <span>(лучше ночью, когда опросов нет)</span>
                    </div>
                    <div className="nl-inline bkp-row">
                        <span>На сервере хранить ежедневные за последние</span>
                        <input type="number" min="1" max="365" className="nl-num" value={form.keep_days}
                               onChange={(e) => setForm({ ...form, keep_days: e.target.value })} aria-label="Дней" />
                        <span>дн. и по одной за последние</span>
                        <input type="number" min="0" max="60" className="nl-num" value={form.keep_months}
                               onChange={(e) => setForm({ ...form, keep_months: e.target.value })} aria-label="Месяцев" />
                        <span>мес.</span>
                    </div>
                    <label className="nl-check bkp-row">
                        <input type="checkbox" checked={form.hourly} onChange={(e) => setForm({ ...form, hourly: e.target.checked })} />
                        Почасовые копии в течение дня — если в базе что-то менялось
                    </label>
                    {form.hourly && (
                        <div className="nl-inline bkp-row">
                            <span>Хранить почасовые на сервере</span>
                            <input type="number" min="1" max="336" className="nl-num" value={form.keep_hours}
                                   onChange={(e) => setForm({ ...form, keep_hours: e.target.value })} aria-label="Часов на сервере" />
                            <span>ч., в облаке</span>
                            <input type="number" min="1" max="1440" className="nl-num" value={form.remote_keep_hours}
                                   onChange={(e) => setForm({ ...form, remote_keep_hours: e.target.value })} aria-label="Часов в облаке" />
                            <span>ч.</span>
                        </div>
                    )}
                    <label className="nl-check bkp-row">
                        <input type="checkbox" checked={form.logs} onChange={(e) => setForm({ ...form, logs: e.target.checked })} />
                        Собирать логи служб каждый час (на сервере — 7 дней, в облаке — 90 дней)
                    </label>
                    {form.logs && data.logs && !data.logs.nginx?.length && (
                        <p className="nl-hint nl-hint--small">
                            Логи nginx недоступны серверу СВОД — сохраняются только логи сервера и ботов. Чтобы добавить nginx,
                            выполните на сервере: <code>{data.logs.nginx_hint}</code>
                        </p>
                    )}
                    <div className="nl-inline bkp-row">
                        <span>В облаке хранить ежедневные</span>
                        <input type="number" min="1" max="3650" className="nl-num" value={form.remote_keep_days}
                               onChange={(e) => setForm({ ...form, remote_keep_days: e.target.value })} aria-label="Дней в облаке" />
                        <span>дн. и по одной за</span>
                        <input type="number" min="0" max="120" className="nl-num" value={form.remote_keep_months}
                               onChange={(e) => setForm({ ...form, remote_keep_months: e.target.value })} aria-label="Месяцев в облаке" />
                        <span>мес.</span>
                    </div>
                    <details className="mail-advanced">
                        <summary>Папка для копий</summary>
                        <input type="text" className="bkp-dir" placeholder={data.settings.dir_custom ? '' : data.settings.dir}
                               value={form.dir} onChange={(e) => setForm({ ...form, dir: e.target.value })} aria-label="Папка для копий" />
                        <p className="nl-hint nl-hint--small">Полный путь. Пусто — {data.settings.dir_custom ? '~/svod_backups' : data.settings.dir}. Можно указать подключённый второй диск.</p>
                    </details>
                    <button type="button" className="btn btn--success" onClick={saveSettings} disabled={busy}>Сохранить</button>
                </div>

                <AdminBackupCloud cloud={data.offsite} onChanged={apply} />

                <div className="nl-block">
                    <h3 className="nl-block__title">Как восстановить из копии</h3>
                    <p className="nl-hint">
                        Восстановление заменяет текущие данные данными из копии — всё, что появилось после неё, пропадёт.
                        Поэтому оно делается вручную в терминале сервера. Перед восстановлением сделайте свежую копию.
                    </p>
                    <button type="button" className="nl-link nl-link--inline" onClick={() => setShowRestore((v) => !v)}>
                        {showRestore ? 'Скрыть команды' : 'Показать команды'}
                    </button>
                    {showRestore && data.restore && (
                        <div>
                            {data.restore.decrypt && <Commands title="Копия из облака — сначала расшифровать (файл .enc, скачанный с Яндекс Диска или из хранилища):" text={data.restore.decrypt} />}
                            {data.restore.check && <Commands title="1. Проверить, что копия читается:" text={data.restore.check} />}
                            <Commands title="2. Восстановить базу (вместо ИМЯ_КОПИИ — имя файла из списка, без .dump):" text={data.restore.db} />
                            {data.restore.static && <Commands title="3. Восстановить картинки:" text={data.restore.static} />}
                            {data.restore.download && <Commands title="Скачать копию на свой компьютер через терминал (выполнять на компьютере):" text={data.restore.download} />}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default AdminBackups;
